/**
 * WorkspaceDeleteDialog - Confirmation dialog for workspace deletion
 * Handles the delete confirmation flow with safety checks
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface WorkspaceDeleteDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceToDelete: any | null;
  confirmationText: string;
  onConfirmationTextChange: (text: string) => void;
  isDeleting: boolean;
  onConfirmDelete: () => void;
  onCancel: () => void;
}

const WorkspaceDeleteDialog: React.FC<WorkspaceDeleteDialogProps> = ({
  isOpen,
  onOpenChange,
  workspaceToDelete,
  confirmationText,
  onConfirmationTextChange,
  isDeleting,
  onConfirmDelete,
  onCancel
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && confirmationText.toLowerCase() === 'delete') {
      onConfirmDelete();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Workspace</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove the workspace <strong>"{workspaceToDelete?.name}"</strong>? 
            <br /><br />
            <strong>This action cannot be undone.</strong> The workspace and its data will be preserved but removed from your account.
            <br /><br />
            To confirm, please type <strong>delete</strong> in the field below:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="confirm-delete">Type "delete" to confirm</Label>
            <Input
              id="confirm-delete"
              value={confirmationText}
              onChange={(e) => onConfirmationTextChange(e.target.value)}
              placeholder="Type delete here"
              className="border-destructive focus:border-destructive focus:ring-destructive"
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirmDelete}
            disabled={isDeleting || workspaceToDelete?.is_default || confirmationText.toLowerCase() !== 'delete'}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              'Remove Workspace'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default WorkspaceDeleteDialog;